import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { CadInternalTransferResponse } from "@services/models/Prime/CadInternalTransferModel";
import { ChangeEvent, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import SettledAccountReponseSlider from "../../settleaccounts/components/SettledAccountReponseSlider";

interface CadInternalTransferPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const CadInternalTransferPanel = ({
  isOpen,
  onClose,
}: CadInternalTransferPanelProps) => {
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amountError, setAmountError] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [responseData, setResponseData] = useState<CadInternalTransferResponse>(
    {},
  );
  const [responseIsSuccess, setResponseIsSuccess] = useState(true);
  const [responseErrorMessage, setResponseErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setAmountError("");
      setIsSubmitting(false);
      setShowResponse(false);
      setResponseData({});
      setResponseIsSuccess(true);
      setResponseErrorMessage("");
    }
  }, [isOpen]);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    if (amountError) {
      setAmountError("");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleContinue = async () => {
    if (!amount || Number(amount) <= 0) {
      setAmountError("A valid positive amount is required.");
      return;
    }

    setIsSubmitting(true);
    setAmountError("");

    try {
      const response = await primeServices.cadInternalTransfer({
        amount: Number(amount),
      });
      setResponseData(response);
      setResponseIsSuccess(response?.ok !== false);
      setResponseErrorMessage(
        response?.ok === false ? response?.msg || "Transfer failed" : "",
      );
    } catch (error: any) {
      const errorPayload = error?.response?.data ||
        error?.data || {
          ok: false,
          msg: error?.message || "Internal transfer failed",
        };
      setResponseData(errorPayload);
      setResponseIsSuccess(false);
      setResponseErrorMessage(
        errorPayload?.msg ||
          error?.message ||
          "Internal transfer request failed",
      );
    } finally {
      setIsSubmitting(false);
      setShowResponse(true);
    }
  };

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <>
      <SlidingPanel
        isOpen={isOpen && !showResponse}
        onClose={handleClose}
        heading="Internal Transfer"
        showConfirm={false}
        isMobile={isMobile}
      >
        <div className="p-[12px] flex flex-col gap-[16px] max-h-[80vh] overflow-y-auto">
          <p className="text-[14px] font-semibold text-text-base">
            Transfer Details
          </p>

          <GlobalTextInputField
            title="1. Transfer from:"
            value="TYLT FINTEC INC"
            onChange={() => {}}
            readonly
            prefixIcon={
              <span className="material-symbols-outlined text-[18px] text-icon-blue-light">
                account_balance
              </span>
            }
            prefixIconPadding="pl-[36px]"
          />
          <GlobalTextInputField
            title="2. Transfer from Account:"
            value="Apaylo"
            onChange={() => {}}
            readonly
            prefixIcon={
              <span className="material-symbols-outlined text-[18px] text-icon-blue-light">
                account_balance_wallet
              </span>
            }
            prefixIconPadding="pl-[36px]"
          />
          <GlobalTextInputField
            title="3. Transfer to:"
            value="AQUANOW INC"
            onChange={() => {}}
            readonly
            prefixIcon={
              <span className="material-symbols-outlined text-[18px] text-icon-status-green">
                account_balance
              </span>
            }
            prefixIconPadding="pl-[36px]"
          />
          <GlobalTextInputField
            title="4. Transfer to Account:"
            value="Apaylo"
            onChange={() => {}}
            readonly
            prefixIcon={
              <span className="material-symbols-outlined text-[18px] text-icon-status-green">
                account_balance_wallet
              </span>
            }
            prefixIconPadding="pl-[36px]"
          />

          <div className="flex flex-col gap-[6px]">
            <GlobalTextInputField
              title="5. Amount:"
              type="number"
              allowDecimal={false}
              value={amount}
              onChange={handleAmountChange}
              placeholder="Enter amount (integers only)"
              suffixIcon={
                <span className="material-symbols-outlined text-[16px] text-text-subtle">
                  payments
                </span>
              }
              suffixIconPadding="pr-[12px]"
            />
            <p className="text-[11px] text-text-subtle">
              Only integers are allowed.
            </p>
            {amountError && (
              <p className="text-[11px] text-text-status-red">{amountError}</p>
            )}
          </div>

          <CustomButton
            text="Continue"
            isActive={!!amount && Number(amount) > 0}
            isButtonLoading={isSubmitting}
            onClick={handleContinue}
            className="w-full mt-[4px]"
          />
        </div>
      </SlidingPanel>

      <SettledAccountReponseSlider
        isOpen={isOpen && showResponse}
        onClose={handleClose}
        data={responseData}
        isSuccess={responseIsSuccess}
        errorMessage={responseErrorMessage}
        title="Response: Internal Transfer"
      />
    </>
  );
};

export default CadInternalTransferPanel;
